/**
 * Phase 7B — prevent premature navigation away from /call/:callId during active Web startup.
 */

const ACTIVE_CALL_STATUSES = new Set(["ongoing", "ringing", "accepted"]);

const TERMINAL_CALL_STATUSES = new Set([
  "ended",
  "cancelled",
  "canceled",
  "timeout",
  "rejected",
  "failed",
  "missed",
]);

export function isActiveCallStatus(status) {
  return ACTIVE_CALL_STATUSES.has(String(status || "").toLowerCase());
}

export function isOnCallRoomRoute(callId, pathname = null) {
  const path =
    pathname != null
      ? String(pathname)
      : typeof window !== "undefined"
        ? String(window.location?.pathname || "")
        : "";
  return path === `/call/${String(callId || "")}`;
}

export function isTerminalCallNavigationStatus(data) {
  if (!data) return false;
  const status = String(data.status || "").toLowerCase();
  if (TERMINAL_CALL_STATUSES.has(status)) return true;
  const endedAt = data.endedAt;
  if (endedAt && typeof endedAt.toMillis === "function") return true;
  return false;
}

export function isExplicitCallExitSource(source = "") {
  const s = String(source || "").toLowerCase();
  return (
    s.includes("hangup") ||
    s.includes("user_clicked") ||
    s.includes("report_block") ||
    s.includes("terminal") ||
    s.includes("heartbeat:ended") ||
    s.includes("remote_ended") ||
    s.includes("remote hangup") ||
    s.includes("native_hangup") ||
    s.includes("hangupsync")
  );
}

export function shouldBlockCallRoomNavigation({
  callId,
  target = "",
  reason = "",
  callStatus = "",
  observedStatus = "",
  exitTriggered = false,
  userInitiated = false,
  invokeEndCall = false,
  webStartupInProgress = false,
  hasWebStartupSession = false,
} = {}) {
  if (userInitiated || invokeEndCall) return false;
  if (isExplicitCallExitSource(reason)) return false;
  if (isTerminalCallNavigationStatus({ status: observedStatus })) return false;
  if (exitTriggered && !isActiveCallStatus(callStatus)) return false;

  const callStillLive =
    isActiveCallStatus(callStatus) || webStartupInProgress || hasWebStartupSession;
  if (!callStillLive) return false;

  if (isTerminalCallNavigationStatus({ status: callStatus })) return false;

  if (!isOnCallRoomRoute(callId) && !webStartupInProgress && !hasWebStartupSession) {
    return false;
  }

  return true;
}

export function shouldBlockCallRoomExit({
  source = "",
  callStatus = "",
  observedStatus = "",
  webStartupInProgress = false,
  hasWebStartupSession = false,
  invokeEndCall = false,
} = {}) {
  if (invokeEndCall) return false;
  if (isExplicitCallExitSource(source)) return false;
  if (isTerminalCallNavigationStatus({ status: callStatus })) return false;
  if (isTerminalCallNavigationStatus({ status: observedStatus })) return false;

  return (
    isActiveCallStatus(callStatus) || webStartupInProgress || hasWebStartupSession
  );
}

export function logBlockedCallNavigation({ callId, target, reason }) {
  console.log(
    `[callNavigationGuard] blocked navigation during active call callId=${String(callId || "")} target=${String(target || "")} reason=${String(reason || "")}`
  );
}

export function logBlockedCallExit({ callId, source }) {
  console.log(
    `[callNavigationGuard] blocked exit during active call callId=${String(callId || "")} source=${String(source || "")}`
  );
}

export function logUnexpectedRouteLeave({ callId, from, to }) {
  console.log(
    `[callNavigationGuard] unexpected route leave during active call callId=${String(callId || "")} from=${String(from || "")} to=${String(to || "")}`
  );
}

export function logCallNavigationGuardOk(message) {
  console.log(`[callNavigationGuard] ${message}`);
}
